<template>
  <div
    class="engineer-template"
    v-if="getPermission('overtime-management:view')"
  >
    <PageHeaderCount
      moduleType="overtime-management"
      :dataLoading="dataLoading"
      :statusList="statusList"
      allkey="all_ot_records"
      countkey="ot_status_count"
    ></PageHeaderCount>

    <ListingTemplate :customClass="'overtime-management'">
      <template v-slot:body>
        <ListingHeader>
          <template v-slot:content>
            <v-flex class="pt-0 mxw-200">
              <h1 class="form-title d-flex margin-auto">
                <v-select
                  :disabled="dataLoading"
                  :items="statusList"
                  v-model="status"
                  hide-details
                  item-color="cyan"
                  class="pt-0 mt-0 listing-status main-listing-status-filter"
                  item-text="text"
                  item-value="value"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title v-text="item.text"></v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-chip :color="item.color" :text-color="item.textcolor">
                        <template v-if="item.value == 'all'">{{
                          item.all_ot_records
                        }}</template>
                        <template v-else>{{ item.ot_status_count }}</template>
                      </v-chip>
                    </v-list-item-action>
                  </template>
                </v-select>
              </h1>
            </v-flex>

            <v-flex
              class="pt-0 justify-flex-end d-flex margin-auto flex-wrap align-center"
            >
              <div class="mx-2" style="min-width: 220px; max-width: 260px;">
                <v-autocomplete
                  dense
                  filled
                  solo
                  flat
                  hide-details
                  clearable
                  item-color="cyan"
                  color="cyan"
                  :disabled="dataLoading || engineerLoading"
                  :loading="engineerLoading"
                  :items="engineerList"
                  item-text="display_name"
                  item-value="display_name"
                  placeholder="Select Technician"
                  v-model.trim="listingSearch.technician_name"
                  v-on:change="filterRows"
                >
                  <template v-slot:no-data>
                    <v-list-item>
                      <v-list-item-title>No Technician(s) Found.</v-list-item-title>
                    </v-list-item>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span class="text-capitalize font-size-13">{{
                      item.display_name
                    }}</span>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title class="font-size-14 text-capitalize">
                        {{ item.display_name }}
                      </v-list-item-title>
                      <v-list-item-subtitle
                        class="font-size-11 text-muted pl-3"
                        v-if="item.barcode"
                      >
                        {{ item.barcode }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </div>

              <v-btn
                depressed
                :disabled="dataLoading"
                class="mx-2 custom-bold-button"
                v-on:click="exportOverTime"
                color="cyan white--text"
              >
                <v-icon dark left>mdi-database-export</v-icon> Export
              </v-btn>
              <v-btn
                depressed
                :disabled="dataLoading"
                class="mx-2 custom-grey-border custom-bold-button"
                :color="
                  isFiltered ? 'cyan darken-2 white--text' : 'cyan white--text'
                "
                v-on:click="searchDialog = true"
              >
                <v-icon dark>mdi-filter</v-icon>
              </v-btn>

              <v-menu
                max-height="400"
                max-width="250"
                offset-y
                left
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    depressed
                    :disabled="dataLoading"
                    class="mx-2 custom-bold-button"
                    color="cyan white--text"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon dark>mdi-table-edit</v-icon>
                  </v-btn>
                </template>
                <draggable
                  tag="ul"
                  v-model="defaultColDefs"
                  v-on:change="updateVisible()"
                  class="draggable-group"
                  handle=".draggable-drag-icon"
                >
                  <template v-for="cols in defaultColDefs">
                    <li
                      class="draggable-group-item"
                      :key="cols.field"
                      v-if="!cols.checkbox || Number(cols.checkbox) == 0"
                    >
                      <v-checkbox
                        dense
                        v-model="defaultColShow"
                        v-bind:value="cols.field"
                        :label="cols.headerName"
                        :disabled="Number(cols.fixed) || dataLoading"
                        color="cyan"
                        v-on:change="updateVisible()"
                        hide-details
                        class="pt-2 mt-0"
                      ></v-checkbox>
                      <v-icon
                        class="draggable-drag-icon"
                        small
                        right
                        color="cyan"
                        >mdi-drag</v-icon
                      >
                    </li>
                  </template>
                </draggable>
              </v-menu>
            </v-flex>
          </template>
        </ListingHeader>

        <ListingSearchCriteria
          v-if="searchEnabled"
          :searchEnabled="searchEnabled"
          pageModule="OverTime"
          :basicSearchFields="[
            'dates',
            'technician_name',
            'technician_barcode',
            'visit_barcode',
          ]"
          :result="totalRows"
          :allowedSearchFields="searchableArray"
        ></ListingSearchCriteria>

        <perfect-scrollbar
          :options="{ suppressScrollX: true }"
          class="scroll custom-box-top-inner-shadow"
          style="position: relative"
        >
          <ListingTable
            :columnCount="columnLength()"
            :dataLoading="dataLoading"
            :rowData="rowData"
          >
            <template v-slot:thead>
              <thead>
                <tr>
                  <template v-for="cols in customThead">
                    <th
                      v-if="cols.visible"
                      :key="cols.id"
                      class="simple-table-th"
                      :class="{
                        'checkbox-width-limit': Number(cols.checkbox),
                        'pointer-events-none': lodash.isEmpty(rowData),
                      }"
                      :style="{
                        'max-width': cols.width
                          ? isNaN(cols.width)
                            ? cols.width
                            : cols.width + 'px'
                          : 'auto',
                      }"
                    >
                      <template
                        v-if="cols.checkbox && Number(cols.checkbox) == 1"
                      >
                        <v-checkbox
                          dense
                          :disabled="dataLoading"
                          v-model="selectedAll"
                          color="cyan"
                          class="hide-margin"
                          hide-details
                        ></v-checkbox>
                      </template>
                      <template v-else>
                        <div
                          v-if="cols.sortable"
                          class="simple-table-text sortable"
                          v-on:click="
                            !dataLoading
                              ? updateSorting({
                                  field: cols.field,
                                  sort: cols.sort,
                                })
                              : 'javascript:void(0)'
                          "
                        >
                          {{ cols.headerName }}
                        </div>
                        <div v-else class="simple-table-text">
                          {{ cols.headerName }}
                        </div>
                        <template v-if="cols.sort">
                          <v-icon
                            class="simple-table-sort"
                            v-if="cols.sort == 'asc'"
                            small
                            right
                            color="cyan"
                            >mdi-sort-ascending</v-icon
                          >
                          <v-icon
                            class="simple-table-sort"
                            v-if="cols.sort == 'desc'"
                            small
                            right
                            color="cyan"
                            >mdi-sort-descending</v-icon
                          >
                        </template>
                      </template>
                    </th>
                  </template>
                </tr>
              </thead>
            </template>
            <template v-slot:tbody>
              <tbody>
                <template v-if="rowData.length > 0">
                  <template v-for="(group, gIndex) in rowData">
                    <tr
                      v-for="(row, rIndex) in group.rows"
                      :key="gIndex + '-' + rIndex"
                      :class="{
                        'orange_lighten-5': gIndex % 2 === 0,
                      }"
                    >
                      <template v-for="cols in customThead">
                        <template v-if="isGroupCol(cols.field)">
                          <td
                            v-if="rIndex === 0 && cols.visible"
                            :key="cols.id || cols.field"
                            :rowspan="group.rows.length"
                            class="simple-table-td"
                          >
                            <template v-if="cols.field === 'image'">
                              <v-avatar size="40">
                                <v-img
                                  class="customer-image white-background"
                                  width="40"
                                  height="40"
                                  :lazy-src="$defaultProfileImage"
                                  :src="
                                    getProfileImage(group.engineer_profile_logo)
                                      ? getProfileImage(
                                          group.engineer_profile_logo
                                        )
                                      : $defaultProfileImage
                                  "
                                >
                                </v-img>
                              </v-avatar>
                            </template>

                            <template
                              v-else-if="cols.field === 'technician_barcode'"
                            >
                              <Barcode
                                :barcode="group.engineer_barcode"
                                route="engineer.detail"
                                :id="group.engineer_id"
                              />
                            </template>

                            <template v-else-if="cols.field === 'technician'">
                              <div class="engineer-listing">
                                <p
                                  v-if="
                                    group.engineer_first_name ||
                                    group.engineer_last_name
                                  "
                                  class="m-0 custom-nowrap-ellipsis text-capitalize font-size-13"
                                >
                                  {{
                                    (group.engineer_title
                                      ? group.engineer_title + ". "
                                      : "") +
                                    (group.engineer_first_name || "") +
                                    " " +
                                    (group.engineer_last_name || "")
                                  }}
                                </p>
                                <p
                                  v-else-if="group.engineer_display_name"
                                  class="m-0 custom-nowrap-ellipsis font-size-13"
                                >
                                  {{ group.engineer_display_name }}
                                </p>
                                <em v-else class="text-muted">No name</em>
                              </div>
                            </template>

                            <template
                              v-else-if="cols.field === 'visit_barcode'"
                            >
                              <Barcode
                                :barcode="group.visit_barcode"
                                route="visit.detail"
                                :id="group.visit_id"
                              />
                            </template>

                            <template
                              v-else-if="cols.field === 'actual_start_date'"
                            >
                              <div
                                class="m-0 max-content-width"
                                v-if="group.actual_start_date || group.actual_end_date"
                              >
                                <p class="m-0">
                                  <span class="m-0 text-truncate">
                                    <b> Started At : </b>
                                    <template v-if="group.actual_start_date">
                                      <v-chip small outlined color="red" label>
                                        {{ formatDateValue(group.actual_start_date) }}
                                        {{ formatDateStartTime(group.actual_start_date) }}
                                      </v-chip>
                                    </template>
                                    <template v-else>
                                      <em class="text-muted"> no started at</em>
                                    </template>
                                  </span>
                                </p>
                                <p class="m-0 mt-1">
                                  <span class="m-0 text-truncate">
                                    <b> Ended At &nbsp;&nbsp;: </b>
                                    <template v-if="group.actual_end_date">
                                      <v-chip small outlined color="green" label>
                                        {{ formatDateValue(group.actual_end_date) }}
                                        {{ formatDateStartTime(group.actual_end_date) }}
                                      </v-chip>
                                    </template>
                                    <template v-else>
                                      <em class="text-muted"> no ended at</em>
                                    </template>
                                  </span>
                                </p>
                              </div>
                              <em v-else class="text-muted">No actual date</em>
                            </template>

                            <template v-else-if="cols.field === 'total_time'">
                              <div v-if="group.is_max_ot_applied">
                                <b>{{ group.final_ot_time }}</b>
                                <div
                                  class="font-size-11 grey--text text-decoration-line-through font-weight-400"
                                >
                                  {{ group.total_time }}
                                </div>
                              </div>
                              <b v-else>{{ group.total_time }}</b>
                            </template>

                            <template v-else-if="cols.field === 'summary'">
                              <div><b>OT 1.5 :</b> {{ group.ot_1_5_time }}</div>
                              <div><b>OT 2.0 :</b> {{ group.ot_2_0_time }}</div>
                              <div>
                                <b>Total OT:</b> {{ group.final_ot_time }}
                              </div>
                              <div v-if="group.is_max_ot_applied" class="mt-1">
                                <v-tooltip
                                  top
                                  content-class="custom-top-tooltip"
                                >
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-chip
                                      x-small
                                      color="orange lighten-4"
                                      text-color="orange darken-4"
                                      class="font-weight-600 px-1 cursor-pointer"
                                      v-bind="attrs"
                                      v-on="on"
                                    >
                                      Max OT Capped ({{ group.max_ot_hours }}h)
                                    </v-chip>
                                  </template>
                                  <span>{{
                                    group.capped_reason ||
                                    `Actual OT was ${group.actual_ot_time} hrs, but capped to ${group.final_ot_time} hrs due to Visit Max OT Limit.`
                                  }}</span>
                                </v-tooltip>
                              </div>
                            </template>
                          </td>
                        </template>

                        <!-- Row-level columns (start_time, end_time, duration, ot_rate, status) -->
                        <template v-else>
                          <td
                            v-if="cols.visible"
                            :key="cols.id || cols.field"
                            class="simple-table-td"
                            :class="{
                              'text-center': [
                                'duration',
                                'ot_rate',
                                'status',
                              ].includes(cols.field),
                            }"
                          >
                            <template v-if="cols.field === 'start_time'">
                              {{ formatOtDateTime(row.ot_start) }}
                            </template>

                            <template v-else-if="cols.field === 'end_time'">
                              {{ formatOtDateTime(row.ot_end) }}
                            </template>

                            <template v-else-if="cols.field === 'duration'">
                              {{ row.total_time || "—" }}
                            </template>

                            <template v-else-if="cols.field === 'ot_rate'">
                              {{ row.rate || "—" }}
                            </template>

                            <template v-else-if="cols.field === 'status'">
                              <v-chip
                                v-if="row.status === 'approved'"
                                color="#00a65a"
                                text-color="white"
                                small
                                label
                                class="font-weight-600 text-uppercase"
                              >
                                Approved
                              </v-chip>

                              <v-chip
                                v-else-if="row.status === 'rejected'"
                                color="#f44336"
                                text-color="white"
                                small
                                label
                                class="font-weight-600 text-uppercase"
                              >
                                Rejected
                              </v-chip>

                              <v-chip
                                v-else
                                color="#f6c23e"
                                text-color="white"
                                small
                                label
                                class="font-weight-600 text-uppercase"
                              >
                                Pending
                              </v-chip>
                            </template>

                            <template v-else-if="cols.field === 'remark'">
                              <template v-if="row.remarks">
                                <v-tooltip
                                  top
                                  max-width="350"
                                  content-class="custom-top-tooltip"
                                >
                                  <template v-slot:activator="{ on, attrs }">
                                    <span
                                      v-bind="attrs"
                                      v-on="on"
                                      class="remark-cell-text cursor-pointer d-inline-block font-size-13"
                                    >
                                      {{ row.remarks }}
                                    </span>
                                  </template>
                                  <span style="word-break: break-word">{{
                                    row.remarks
                                  }}</span>
                                </v-tooltip>
                              </template>
                              <span v-else class="text-muted font-size-13">
                                No remarks
                              </span>
                            </template>

                            <template v-else-if="cols.field === 'action'">
                              <v-tooltip top content-class="custom-top-tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                  <span
                                    v-on="on"
                                    v-bind="attrs"
                                    class="d-inline-block"
                                  >
                                    <v-btn
                                      icon
                                      small
                                      color="cyan"
                                      class="mx-1"
                                      @click="UpdateOt(row)"
                                      :disabled="
                                        dataLoading ||
                                        (row.status && row.status !== 'pending')
                                      "
                                    >
                                      <v-icon small>mdi-pencil</v-icon>
                                    </v-btn>
                                  </span>
                                </template>
                                <span>Edit</span>
                              </v-tooltip>

                              <v-tooltip top content-class="custom-top-tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                  <span
                                    v-on="on"
                                    v-bind="attrs"
                                    class="d-inline-block"
                                  >
                                    <v-btn
                                      icon
                                      small
                                      color="green"
                                      class="mx-1"
                                      @click="openStatusDialog(row, 'approved')"
                                      :disabled="
                                        dataLoading ||
                                        !canManageOt(row, group) ||
                                        (row.status && row.status !== 'pending')
                                      "
                                    >
                                      <v-icon small
                                        >mdi-check-circle-outline</v-icon
                                      >
                                    </v-btn>
                                  </span>
                                </template>
                                <span>{{
                                  !canManageOt(row, group)
                                    ? "SBU is not match"
                                    : "Approve"
                                }}</span>
                              </v-tooltip>

                              <v-tooltip top content-class="custom-top-tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                  <span
                                    v-on="on"
                                    v-bind="attrs"
                                    class="d-inline-block"
                                  >
                                    <v-btn
                                      icon
                                      small
                                      color="red"
                                      class="mx-1"
                                      @click="openStatusDialog(row, 'rejected')"
                                      :disabled="
                                        dataLoading ||
                                        !canManageOt(row, group) ||
                                        (row.status && row.status !== 'pending')
                                      "
                                    >
                                      <v-icon small
                                        >mdi-close-circle-outline</v-icon
                                      >
                                    </v-btn>
                                  </span>
                                </template>
                                <span>{{
                                  !canManageOt(row, group)
                                    ? "SBU is not match"
                                    : "Reject"
                                }}</span>
                              </v-tooltip>
                            </template>
                          </td>
                        </template>
                      </template>
                    </tr>
                  </template>
                </template>

                <template v-else-if="!dataLoading">
                  <tr>
                    <td :colspan="customThead.length">
                      <p
                        class="m-0 row-not-found text-center font-weight-500 font-size-16"
                      >
                        <img
                          :src="$assetURL('media/error/empty.png')"
                          class="row-not-found-image"
                        />
                        Uhh... There are no OT records at the moment.
                      </p>
                    </td>
                  </tr>
                </template>
              </tbody>
            </template>
          </ListingTable>
        </perfect-scrollbar>

        <ListingFooter
          :dataLoading="dataLoading"
          :showingFrom="showingFrom"
          :showingTo="showingTo"
          :totalRows="totalRows"
          :currentPage="currentPage"
          :totalPages="totalPages"
        ></ListingFooter>

        <v-navigation-drawer
          v-model="searchDialog"
          app
          bottom
          temporary
          stateless
          right
          :width="600"
          style="z-index: 500"
        >
          <v-card
            text
            elevation="0"
            class="d-flex flex-column h-100 custom-field"
          >
            <v-card-title
              class="headline dialog-custom-header text-uppercase custom-border-bottom custom-header-blue-text white-background"
            >
              <span style="font-size: 18px">Filter OT Records</span>
              <v-spacer></v-spacer>
              <v-btn icon small @click="searchDialog = false">
                <v-icon size="30">mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <v-card-text class="flex-grow-1 px-3">
              <perfect-scrollbar
                :options="{ suppressScrollX: true }"
                class="scroll custom-box-top-inner-shado"
                style="height: calc(100vh - 145px); position: relative"
              >
                <v-container fluid class="pt-0 custom-search-filter">
                  <v-row>
                    <v-col md="12" cols="12">
                      <label
                        class="v-label font-weight-500 font-size-13 text-capitalize"
                        >Date Range</label
                      >
                      <DateRangePicker
                        placeholder="Date Range"
                        v-model="dates"
                        v-on:click:clear="dates = []"
                      ></DateRangePicker>
                    </v-col>

                    <template v-for="(field, index) in searchableArray">
                      <v-col
                        md="12"
                        :key="index"
                        v-if="
                          field != 'dates' &&
                          field != 'start_date' &&
                          field != 'end_date'
                        "
                      >
                        <label
                          class="v-label font-weight-500 font-size-13 text-capitalize"
                          >{{ getFilterLabel(field, "Employee") }}</label
                        >
                        <v-text-field
                          v-model.trim="listingSearch[field]"
                          dense
                          filled
                          hide-details
                          :placeholder="getFilterLabel(field, 'Employee')"
                          solo
                          flat
                          clearable
                          @keydown.enter="filterRows"
                          color="cyan"
                        ></v-text-field>
                      </v-col>
                    </template>
                  </v-row>
                </v-container>
              </perfect-scrollbar>
            </v-card-text>

            <v-divider class="my-0"></v-divider>
            <v-card-actions class="d-flex justify-start px-4 pb-4">
              <v-btn
                depressed
                :disabled="dataLoading"
                class="mx-2 custom-grey-border custom-bold-button"
                color="red white--text"
                v-on:click="removeFilters('clear')"
              >
                <v-icon left>mdi-close</v-icon>
                <span>Clear Filter</span>
              </v-btn>
              <v-btn
                depressed
                :disabled="dataLoading"
                class="mx-2 custom-grey-border custom-bold-button"
                color="cyan white--text"
                v-on:click="filterRows"
              >
                <v-icon left>mdi-magnify</v-icon>
                <span>Apply Filter</span>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-navigation-drawer>

        <UpdateOT
          :document-dialog="ot_dialog"
          :ot-row="otRow"
          @close="ot_dialog = false"
          @success="getRows()"
        />
        <OtApprovedRejectDialog
          :status-dialog="statusDialog"
          :status="selectedStatus"
          :ot-data="selectedOtGroup"
          @close="statusDialog = false"
          @success="getRows()"
        />
      </template>
    </ListingTemplate>
  </div>
</template>

<script>
import PageHeaderCount from "@/view/pages/partials/PageHeaderCount.vue";
import CommonMixin from "@/core/plugins/common-mixin";
import ListingMixin from "@/core/plugins/listing-mixin";
import ListingTemplate from "@/view/pages/partials/Listing-Template.vue";
import ListingTable from "@/view/pages/partials/Listing-Table.vue";
import ListingHeader from "@/view/pages/partials/Listing-Header.vue";
import ListingFooter from "@/view/pages/partials/Listing-Footer.vue";
import { SearchEventBus } from "@/core/lib/search.lib";
import DateRangePicker from "@/view/components/DateRangePicker.vue";
import moment from "moment-timezone";
import Barcode from "@/view/pages/partials/Barcode.vue";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import draggable from "vuedraggable";
import UpdateOT from "@/view/pages/engineer/Update-Ot";
import OtApprovedRejectDialog from "@/view/pages/partials/Ot-Approved-Reject-Dialog.vue";
import ListingSearchCriteria from "@/view/pages/partials/Listing-Search-Criteria.vue";
import JwtService from "@/core/services/jwt.service";
import { QUERY } from "@/core/services/store/request.module";

export default {
  name: "overtime-management",
  mixins: [CommonMixin, ListingMixin],
  components: {
    PageHeaderCount,
    ListingTemplate,
    ListingTable,
    ListingHeader,
    ListingFooter,
    DateRangePicker,
    Barcode,
    draggable,
    UpdateOT,
    OtApprovedRejectDialog,
    ListingSearchCriteria,
  },
  data() {
    return {
      pageModule: "overtime-management",
      routeAPI: "ot-listing",
      routeName: "overtime-management",
      status: "all",
      otRow: {},
      statusDialog: false,
      ot_dialog: false,
      selectedStatus: "",
      selectedOtGroup: {},
      statusList: [],
      customThead: [],
      engineerList: [],
      engineerLoading: false,
    };
  },
  watch: {
    dates(newDates) {
      if (newDates && newDates.length) {
        this.listingSearch.dates = newDates;
      } else {
        delete this.listingSearch.dates;
      }
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "Overtime Management" }]);
    this.paginationPageSize =
      window.localStorage.getItem(this.pageModule) || 10;
    this.getEngineerList();
  },
  computed: {
    isFiltered() {
      return (
        (this.dates && this.dates.length > 0) ||
        Object.keys(this.listingSearch || {}).some(
          (k) => !!this.listingSearch[k]
        )
      );
    },
  },
  methods: {
    removeFilters() {
      this.dates = [];
      SearchEventBus.$emit("clear-search", true);
    },
    getProfileImage(profile_logo) {
      if (!profile_logo) return null;
      if (typeof profile_logo === "string" && profile_logo.length > 0) {
        return profile_logo;
      }
      if (profile_logo.file && profile_logo.file.url) {
        return profile_logo.file.url;
      }
      if (profile_logo.url) {
        return profile_logo.url;
      }
      return null;
    },
    formatDateTime(dateTime) {
      if (!dateTime) return "—";
      return moment(dateTime).format("DD-MM-YYYY hh:mm A");
    },
    isGroupCol(field) {
      return [
        "image",
        "technician_barcode",
        "technician",
        "visit_barcode",
        "actual_start_date",
        "total_time",
        "summary",
      ].includes(field);
    },
    formatDateValue(date) {
      if (!date) return "";
      const m = moment(date);
      if (!m.isValid()) return "";
      const dateFormat = this.dateFormat || "DD/MM/YYYY";
      return m.format(dateFormat);
    },
    formatDateStartTime(date) {
      if (!date) return "";
      const m = moment(date);
      if (!m.isValid()) return "";
      return m.format("hh:mm A");
    },
    canManageOt(row, group = {}) {
      const admin_user = JwtService.currentUser();
      if (!admin_user) {
        return false;
      }
      const isSbuHead =
        admin_user.is_sbu_head === true ||
        admin_user.is_sbu_head === 1 ||
        admin_user.is_sbu_head == "1";
      const userSbu = admin_user.sbu ? Number(admin_user.sbu) : null;
      const technicianSbu = row?.technician_sbu
        ? Number(row.technician_sbu)
        : group?.technician_sbu
        ? Number(group.technician_sbu)
        : null;

      if (isSbuHead && userSbu && technicianSbu && userSbu === technicianSbu) {
        return true;
      }
      return false;
    },
    UpdateOt(row) {
      this.otRow = row;
      this.ot_dialog = true;
    },
    openStatusDialog(group, status) {
      this.selectedOtGroup = group;
      this.selectedStatus = status;
      this.statusDialog = true;
    },
    exportOverTime() {
      let downloadURL = process.env.VUE_APP_API_URL + "overTime/export";
      downloadURL = new URL(downloadURL);
      const token = JwtService.getToken();
      downloadURL.searchParams.append("token", token);

      if (this.searchParam) {
        downloadURL.searchParams.append("search", this.searchParam);
      }
      if (this.status && this.status !== "all") {
        downloadURL.searchParams.append("status", this.status);
      }
      if (this.dates && this.dates[0] && this.dates[1]) {
        downloadURL.searchParams.append("start_date", this.dates[0]);
        downloadURL.searchParams.append("end_date", this.dates[1]);
        downloadURL.searchParams.append("dates[0]", this.dates[0]);
        downloadURL.searchParams.append("dates[1]", this.dates[1]);
      }
      this.lodash.map(this.queryParams, function (row, index) {
        if (row) {
          downloadURL.searchParams.append(index, row);
        }
      });
      this.lodash.map(this.listingSearch, function (row, index) {
        if (index === "dates") {
          if (row && row[0]) {
            downloadURL.searchParams.append("dates[0]", row[0]);
          }
          if (row && row[1]) {
            downloadURL.searchParams.append("dates[1]", row[1]);
          }
        } else if (row) {
          downloadURL.searchParams.append(index, row);
        }
      });

      window.open(downloadURL, "_blank");
    },
    getFilterLabel(field) {
      const labelMap = {
        technician_name : "Technician Name",
        technician_barcode: "Technician #",
        visit_barcode: "Visit #",
      };
      return labelMap[field] || field.split("_").join(" ");
    },
    getEngineerList() {
      const _this = this;
      _this.engineerLoading = true;
      _this.$store
        .dispatch(QUERY, {
          url: "engineer-list",
        })
        .then(({ data }) => {
          _this.engineerList = data || [];
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.engineerLoading = false;
        });

      },
  },
};
</script>

<style scoped>
::v-deep .v-data-table tbody tr:not(.orange_lighten-5):hover {
  background: transparent !important;
}

::v-deep .custom-listing-table .simple-table table tbody tr td {
  border-bottom: thin solid rgba(0, 0, 0, 0.12) !important;
}

::v-deep .v-data-table button.v-btn.v-btn--disabled,
::v-deep button.v-btn.v-btn--icon.v-btn--disabled {
  border: none !important;
}

.remark-cell-text {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
</style>
